// iOS の PWA（ホーム画面から起動）では、ビューポートが画面全体に広がることがある。
// このとき position:fixed のヘッダー（top: 0）はステータスバーや Dynamic Island の
// 下に潜り込むが、env(safe-area-inset-top) は 0 のままなので CSS 側で避けられない。
//
// 実機（iPhone 16 Pro / iOS 27.0）の実測値:
//   正常時   innerHeight 812 / screen.height 874 / env(top) 0   → 差 62 がステータスバー
//   異常時   innerHeight 874 / screen.height 874 / env(top) 0   → 差が消える（22.6秒 持続）
//
// きっかけはメモリ圧迫（ブラウザのタブを大量に開いた後など）。WebView が破棄され、
// 復帰時に再読み込みされる。その直後が既に異常だと正常時を観測できないため、
// 測った値は localStorage に持ち越す。
//
// 正常時にだけ測れる screen.height - innerHeight を保持しておき、ビューポートが
// 広がっている間だけ CSS 変数として供給する。CSS 側は
// max(env(safe-area-inset-top), var(--status-bar-inset, 0px)) で受けるので、
// env が正しく返る環境ではそちらが優先され、正常時は両方 0 で従来と変わらない。
//
// ref: https://gitlab.com/atma_inc/tasukeru-today-front/-/issues/660

const CSS_VAR = '--status-bar-inset'
const STORAGE_KEY = 'statusBarInsetPx'

// ステータスバーとして妥当な範囲。これを超える差はキーボードなど別の要因
const MAX_PLAUSIBLE_INSET = 200

// 差を基準として採用する前に、この時間だけ同じ値が続くことを要求する。
// ビューポートが 812 → 874 へ変わる途中の中間値が resize / visualViewport.resize に
// 乗ることがあり、それを掴むと基準が狂う（下方向にしか動かないため）
const STABLE_MS = 500

function isPlausible(px) {
  return typeof px === 'number' && isFinite(px) && px > 0 && px < MAX_PLAUSIBLE_INSET
}

// 基準は測ったときの画面の高さと組で持つ。寸法が違う文脈の値を使うと
// 過剰または過小な補正になる。
// 旧形式（数値だけ）は screenH を持たないので自然に無視される。
function restore(win) {
  try {
    const saved = JSON.parse(win.localStorage.getItem(STORAGE_KEY))
    if (!saved || typeof saved.screenH !== 'number' || !isPlausible(saved.gap)) return null
    return saved
  } catch (e) {
    return null
  }
}

function persist(win, gap, screenH) {
  try {
    win.localStorage.setItem(STORAGE_KEY, JSON.stringify({ screenH, gap }))
  } catch (e) {
    // 保存できなくても、このページが生きている間は使える
  }
}

// メモリ圧迫で WebView が破棄されると、復帰時にページが再読み込みされる。
// その直後が既に異常だと正常時を観測できないため、測った値を持ち越す。
// 復元には win が要るので、最初の同期まで遅らせる（null = 未復元）。
let knownInset = null
let knownScreenH = null

// 「同じ差が可視状態で STABLE_MS 続いた」ことを数えるための候補。
// 背面では innerHeight が遷移途中の値のまま固定されることがあるので、
// 一定時間後に一点だけ見比べる方式では中間値を掴んでしまう。
let candidateGap = null
let candidateSince = 0
let recheckTimer = null

// CSS 変数を書き込むのはスタイル再計算を伴うので、値が変わったときだけにする。
// root ごとに覚える。同じ値でも別の root にはまだ書かれていないことがある。
const appliedByRoot = new WeakMap()

// ホーム画面から起動したときだけ、screen.height - innerHeight が
// ステータスバーの高さになる。ブラウザ表示ではブラウザ UI の高さになってしまう。
function isStandalone(win) {
  return !!(win.navigator && win.navigator.standalone === true)
}

// 連続性の判定に使う時計。Date.now() は時刻同期などで巻き戻ることがあり、
// 巻き戻ると差が負になって候補が永久に採用されなくなる
function nowMs(win) {
  return win.performance && typeof win.performance.now === 'function'
    ? win.performance.now()
    : Date.now()
}

function isHidden(win) {
  return !!(win.document && win.document.visibilityState === 'hidden')
}

function clearCandidate(win) {
  candidateGap = null
  if (recheckTimer !== null) {
    win.clearTimeout(recheckTimer)
    recheckTimer = null
  }
}

// 観測した最小の差をステータスバーの高さとみなす。通話中や画面収録中は
// ステータスバーが高くなって差が大きくなるため、大きいほうは採らない。
// ただし画面の高さが変われば別の文脈なので、比較せず学習し直す。
// knownInset が 0 のとき knownScreenH は必ず null なので第2項は短絡するが、
// 「基準を持っていないなら無条件に採る」という意図を残しておく。
function adopt(win, gap, screenH) {
  if (knownScreenH === screenH && knownInset !== 0 && gap >= knownInset) return
  knownInset = gap
  knownScreenH = screenH
  persist(win, gap, screenH)
}

// 可視状態で同じ差が STABLE_MS 続いたときだけ採用する。
// タイマーは「採用する」ためではなく「もう一度観測する」ために使う。
// そうしないと、候補を持ったまま背面に回ったときに、遷移途中の値が
// そのまま残っているのを「安定していた」と誤認してしまう。
function observe(win, now) {
  if (isHidden(win)) {
    clearCandidate(win)
    return
  }

  const screenH = win.screen.height
  const gap = screenH - win.innerHeight
  if (!isPlausible(gap)) {
    clearCandidate(win)
    return
  }

  if (gap !== candidateGap) {
    candidateGap = gap
    candidateSince = now
  } else if (now - candidateSince >= STABLE_MS) {
    clearCandidate(win)
    adopt(win, gap, screenH)
    return
  }

  // イベントが来ないまま落ち着くこともあるので、自分でもう一度見に来る
  if (recheckTimer === null) {
    recheckTimer = win.setTimeout(() => {
      recheckTimer = null
      observe(win, nowMs(win))
    }, STABLE_MS)
  }
}

export function syncStatusBarInset(win = window, root = document.documentElement) {
  if (knownInset === null) {
    const saved = restore(win)
    knownInset = saved ? saved.gap : 0
    knownScreenH = saved ? saved.screenH : null
  }

  const innerH = win.innerHeight
  const screenH = win.screen.height
  const standalone = isStandalone(win)
  if (standalone) observe(win, nowMs(win))

  // ビューポートが画面全体に広がっている＝固定ヘッダーが潜り込む状態。
  // 横向きでは innerHeight が screen.height に届かないため自然に対象外になる。
  // 実機では正常時も異常時も screen.height は同じ（874）なので、寸法の一致を
  // 条件にしても補正は効く。外れた場合は補正しないだけで、誤った値は当てない。
  const edgeToEdge = standalone && innerH >= screenH
  const inset = edgeToEdge && knownScreenH === screenH ? knownInset : 0

  if (appliedByRoot.get(root) !== inset) {
    appliedByRoot.set(root, inset)
    root.style.setProperty(CSS_VAR, `${inset}px`)
  }
  return inset
}

export function watchStatusBarInset(win = window, root = document.documentElement) {
  const sync = () => syncStatusBarInset(win, root)
  // 購読したときのオブジェクトを捕まえておく。解除時に読み直すと、
  // 差し替わっていた場合に古いリスナーが残る
  const viewport = win.visualViewport
  const doc = win.document
  const onVisibility = () => {
    if (doc.visibilityState === 'visible') sync()
    else clearCandidate(win)
  }

  sync()
  win.addEventListener('resize', sync)
  win.addEventListener('orientationchange', sync)
  win.addEventListener('pageshow', sync)
  if (viewport) {
    viewport.addEventListener('resize', sync)
  }
  if (doc) {
    doc.addEventListener('visibilitychange', onVisibility)
  }

  // 呼び出し側が破棄できるようにしておく。二重に購読すると、同じイベントで
  // 古いクロージャも動いてしまう
  return () => {
    win.removeEventListener('resize', sync)
    win.removeEventListener('orientationchange', sync)
    win.removeEventListener('pageshow', sync)
    if (viewport) {
      viewport.removeEventListener('resize', sync)
    }
    if (doc) {
      doc.removeEventListener('visibilitychange', onVisibility)
    }
    // 観測中の候補も止める。残しておくと解除後に基準が書き換わる
    clearCandidate(win)
  }
}
